package com.evoapps.evoshift.domain.usecase.capacity

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.capacity.CapacityCalculator
import com.evoapps.evoshift.domain.capacity.CapacityInput
import com.evoapps.evoshift.domain.capacity.CapacitySummary
import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import kotlin.time.Instant

/**
 * The capacity of a roster's month or part of it (SH-48, see [CapacityCalculator]): the shifts,
 * the team's availability and approved absences, and (only when [readPayTerms], i.e. for the owner
 * and financial managers, who may read them) the pay terms behind contracts and hour targets.
 * Managers and the owner may read all of it (firestore.rules: availability, absences, shifts).
 */
class GetRosterCapacityUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val absenceRepository: AbsenceRepository
) {
    suspend operator fun invoke(rosterId: String, range: ScheduleRange? = null, readPayTerms: Boolean): Result<CapacitySummary> = runCatching {
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        val workplace = workplaceRepository.getWorkplace(roster.workplaceId).getOrThrow()
        val month = monthRange(roster.year, roster.month)
        val team = userRepository.getAssignableEmployees(roster.workplaceId).getOrThrow()
            .filter { it.engagements[roster.workplaceId]?.isActive == true }
        val monthIso = roster.getMonthYear()
        val submitted = availabilityRepository.getAvailabilityForRoster(rosterId).getOrDefault(emptyList())
            .filter { it.workplaceId == null || it.workplaceId == roster.workplaceId }
            .associateBy { it.userId }
        val availability: Map<String, Availability> = team.mapNotNull { user ->
            (submitted[user.uid] ?: availabilityRepository.getAvailability(user.uid, roster.workplaceId, monthIso).getOrNull())
                ?.let { user.uid to it }
        }.toMap()
        val absences = absenceRepository.getAbsencesForRoster(roster.workplaceId, month.start.toString(), month.endInclusive.toString())
            .getOrDefault(emptyList())
        val payTerms = if (!readPayTerms) null else team.associate { user ->
            user.uid to userRepository.getSensitiveUserData(user.uid).map { it.financialEngagements[roster.workplaceId] }
        }
        val rules = PayrollRules.of(workplace.settings.market)
        CapacityCalculator.calculate(
            CapacityInput(
                rosterId = rosterId,
                workplaceId = roster.workplaceId,
                workplaceOwnerId = workplace.ownerId,
                year = roster.year,
                month = roster.month,
                range = range,
                shifts = shiftRepository.getShiftsForRoster(rosterId).getOrThrow(),
                team = team,
                availability = availability,
                absences = absences,
                payTerms = payTerms,
                slots = workplace.settings.definedAvailabilitySlots,
                monthNominalHours = rules.workingHours(roster.year, roster.month),
                nominalHours = { rules.workingHours(it.start, it.endInclusive) },
                request = roster.availabilityRequestId?.let { availabilityRepository.getAvailabilityRequest(it).getOrNull() }
            )
        )
    }
}

/**
 * Reminds people who have not sent their availability for a roster's month (SH-48; their sheet
 * e-mailed reminders with the deadline): an availability notification (inbox and push) to each.
 * [title] and [message] are the push text in the sender's language (the app shows its own,
 * localised, from the data). Returns how many were reminded.
 */
class RemindAvailabilityUseCase(
    private val rosterRepository: RosterRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val userRepository: UserRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(rosterId: String, userIds: Collection<String>, title: String, message: String): Result<Int> = runCatching {
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        val request = roster.availabilityRequestId?.let { availabilityRepository.getAvailabilityRequest(it).getOrNull() }
        val sentAt = now().toString()
        var sent = 0
        userIds.distinct().forEach { uid ->
            userRepository.sendNotification(
                Notification(
                    userId = uid,
                    title = title,
                    message = message,
                    type = NotificationType.AVAILABILITY_REQUEST,
                    createdAt = sentAt,
                    data = buildMap {
                        put(NotificationData.ROSTER_ID, rosterId)
                        put(NotificationData.WORKPLACE_ID, roster.workplaceId)
                        put(NotificationData.MONTH, roster.month.toString())
                        put(NotificationData.YEAR, roster.year.toString())
                        put(NotificationData.KIND, NotificationData.Kind.AVAILABILITY_REMINDER)
                        request?.dueDate?.take(10)?.let { put(NotificationData.DATE, it) }
                    }
                )
            ).getOrThrow()
            sent++
        }
        sent
    }
}
